import { type SveaCreateOrderModel, type SveaOrder, type SveaUpdateOrderModel } from 'ecosystem'
import { createLogger } from 'shared-utils'
import type { ApiCallResponse } from '../../fetching'
import { SVEA_API_CHECKOUT_URL } from '../constants'
import { createSveaHeaders } from '../utils'

const logger = createLogger('sveaCheckout')

const CHECKOUT_URL = `${SVEA_API_CHECKOUT_URL}/api/orders`

const headersJson = {
  'Content-Type': 'application/json'
}

/**
 * The fields of an outgoing order that decide whether Svea accepts it at all:
 * the merchant's credentials are provisioned per market, and country, currency
 * and locale have to be a combination that market supports. Logged on failure;
 * cart contents and preset values are deliberately left out.
 */
const summariseRequest = (body: BodyInit | null | undefined) => {
  if (typeof body !== 'string') {
    return undefined
  }

  try {
    const { countryCode, currency, locale, clientOrderNumber } = JSON.parse(body)
    return { countryCode, currency, locale, clientOrderNumber }
  } catch {
    return undefined
  }
}

/** Svea returns a correlation id on failures; keep the whole header set, but
 *  read it without relying on `Headers.entries` (absent from the DOM lib this
 *  package compiles against). */
const sveaResponseHeaders = (headers: Headers): Record<string, string> => {
  const result: Record<string, string> = {}
  headers.forEach((value, key) => {
    result[key] = value
  })
  return result
}

const sveaApiCall = async <Data>(
  url: string,
  { headers, ...props }: RequestInit
): Promise<ApiCallResponse<Data>> => {
  if (url.includes('undefined')) {
    throw new Error('Svea environment variables missing')
  }

  try {
    const response = await fetch(url, {
      headers: {
        ...createSveaHeaders(props.body as string),
        ...headers
      },
      ...props
    })

    /** @see https://checkoutapistage.svea.com/docs/#/order-api */
    const knownErrors = {
      400: 'The input data was invalid.',
      401: 'The request did not contain correct authorization.',
      403: 'The request did not contain correct authorization.',
      404: 'No order with the requested ID was found.'
    }

    if (!(response.status >= 200 && response.status < 300)) {
      // Svea's body names the offending field — which preset value it rejected,
      // which country the identity flow does not support. Without it every
      // failure reads as "Unknown error occurred." and is undiagnosable.
      const body = await response.text().catch(() => '')
      const summary =
        knownErrors[response.status as keyof typeof knownErrors] ??
        `Unknown error occurred (HTTP ${response.status}).`

      // A 5xx usually arrives with no body at all — the useful signal is then
      // the request itself (a country/currency/locale combination the merchant
      // account is not provisioned for) and any correlation id Svea returns in
      // its headers.
      logger.error('Svea request failed', {
        url,
        status: response.status,
        statusText: response.statusText,
        headers: sveaResponseHeaders(response.headers),
        body,
        request: summariseRequest(props.body)
      })

      return {
        data: null,
        error: body ? `${summary} ${body}` : summary
      }
    }

    try {
      const responseData = await response.json()

      return {
        data: responseData,
        error: null
      }
    } catch (e) {
      return {
        data: null,
        error: `PARSING RESPONSE ERROR: ${e as string}`
      }
    }
  } catch (error) {
    return {
      data: null,
      error: `API ERROR: ${error as string}`
    }
  }
}

/**
 * Use this request to create a Checkout order.
 * The response contains the necessary data to embed the Checkout on your site.
 * @see https://checkoutapistage.svea.com/docs/#/order-api?id=create-order
 */
export const createOrder = (payload: SveaCreateOrderModel) =>
  sveaApiCall<SveaOrder>(CHECKOUT_URL, {
    method: 'POST',
    headers: headersJson,
    body: JSON.stringify(payload)
  })

/**
 * Use this request to retrieve an existing Checkout order
 * @see https://checkoutapistage.svea.com/docs/#/order-api?id=get-order
 */
export const retrieveOrder = (orderId: string | number) =>
  sveaApiCall<SveaOrder>(`${CHECKOUT_URL}/${orderId}`, {
    method: 'GET'
  })

/**
 * Use this request to update an existing Checkout order.
 * @remarks
 * Only cart and merchantData can be updated on an existing order.
 * If you need to update other fields, simply create a new Checkout order.
 * @see https://checkoutapistage.svea.com/docs/#/order-api?id=update-order
 */
export const updateOrder = (orderId: string | number, payload: SveaUpdateOrderModel) =>
  sveaApiCall<SveaOrder>(`${CHECKOUT_URL}/${orderId}`, {
    method: 'PUT',
    headers: headersJson,
    body: JSON.stringify(payload)
  })
